import { useEffect, useRef, useState } from "react";
import QuestionLayout from "./QuestionLayout";
import AudioPlayButton from "./AudioPlayButton";
import KeySignatureInputWidget from "./KeySignatureInput.jsx";
import { melodyInput } from "./notation/melodyInput";

/* ─────────────────────────────────────────────
   VALIDATION HELPERS
───────────────────────────────────────────── */

function getDefaultTimeSig(config = {}) {
  return config.tsig ?? config.timeSig ?? "4/4";
}

function getStudentTimeParts(value = {}) {
  const raw = value.tsig ?? value.timeSig ?? "";
  const [top = "", bottom = ""] = String(raw).split("/");

  return {
    top: value.timeSigTop ?? top,
    bottom: value.timeSigBottom ?? bottom
  };
}

function getDraftInitialTimeParts(value = {}) {
  // This modal is for the student's answer. Do not seed it from the
  // internal/default meter in config, or the correct answer appears before
  // the student has entered anything.
  return getStudentTimeParts(value);
}

function getDisplayedTimeSigFromValue(value = {}) {
  const { top, bottom } = getStudentTimeParts(value);

  return isValidTimeSig(top, bottom)
    ? `${top}/${bottom}`
    : "";
}

function isValidTimeSigTop(value) {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 12;
}

function isValidTimeSigBottom(value) {
  const n = Number(value);
  return [1, 2, 4, 8, 16, 32].includes(n);
}

function isValidTimeSig(top, bottom) {
  return isValidTimeSigTop(top) && isValidTimeSigBottom(bottom);
}

function normalizeAccidental(accidental) {
  if (!accidental) return "";
  if (accidental === "♯") return "#";
  if (accidental === "♭") return "b";
  return accidental;
}

function getValidKeySignatureTypeAndCount(accidentals = {}) {
  const slot = accidentals?.[1];

  if (!Array.isArray(slot) || slot.length === 0) {
    return { valid: true, type: "none", count: 0 };
  }

  if (slot.length > 7) {
    return { valid: false, reason: "Too many accidentals" };
  }

  const normalized = slot.map(a => ({
    pitch: a.pitch,
    accidental: normalizeAccidental(a.accidental)
  }));

  const type = normalized[0]?.accidental;

  if (type !== "#" && type !== "b") {
    return {
      valid: false,
      reason: "Key signature must use only sharps or only flats"
    };
  }

  if (!normalized.every(a => a.accidental === type)) {
    return {
      valid: false,
      reason: "Cannot mix sharps and flats in a key signature"
    };
  }

  const expectedOrder =
    type === "#"
      ? ["F", "C", "G", "D", "A", "E", "B"]
      : ["B", "E", "A", "D", "G", "C", "F"];

  const receivedOrder = normalized.map(a => a.pitch?.[0]);

  const correctOrder = receivedOrder.every(
    (letter, i) => letter === expectedOrder[i]
  );

  if (!correctOrder) {
    return {
      valid: false,
      reason: "Key signature accidentals are out of order"
    };
  }

  return {
    valid: true,
    type: type === "#" ? "sharps" : "flats",
    count: slot.length
  };
}

function getMajorKeyFromSignature(type, count) {
  const sharpKeys = [
    "C",
    "G",
    "D",
    "A",
    "E",
    "B",
    "F#",
    "C#"
  ];

  const flatKeys = [
    "C",
    "F",
    `B\u0231`,
    `E\u0231`,
    `A\u0231`,
    `D\u0231`,
    `G\u0231`,
    `C\u0231`
  ];

  if (type === "none") return "C";
  if (type === "sharps") return sharpKeys[count] || "";
  if (type === "flats") return flatKeys[count] || "";

  return "";
}

function getKeyAndModeFromValue(nextValue = {}, config = {}) {
  if (nextValue.key) {
    return {
      key: nextValue.key,
      mode: nextValue.mode || "major"
    };
  }

  const ks =
    nextValue.keySignature ??
    config.keySignature ??
    null;

  if (!ks) {
    return {
      key: config.key || "C",
      mode: config.mode || "major"
    };
  }

  return {
    key: getMajorKeyFromSignature(ks.type, ks.count),
    mode: "major"
  };
}

/* ─────────────────────────────────────────────
   TIME SIGNATURE PREVIEW
───────────────────────────────────────────── */

function TimeSigPreview({ top, bottom }) {
  return (
    <svg
      width="80"
      height="110"
      viewBox="0 0 80 110"
      aria-label="Time signature preview"
      style={{ display: "block" }}
    >
      <TimeSigNumber value={top} y={8} />
      <TimeSigNumber value={bottom} y={58} />
    </svg>
  );
}

function TimeSigNumber({ value, y }) {
  const text = String(value || "");

  return (
    <text
      x="40"
      y={y + 36}
      textAnchor="middle"
      fontSize="42"
      fontWeight="700"
      fontFamily="serif"
    >
      {text}
    </text>
  );
}

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */

export default function MelodyEditorSS({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const containerRef = useRef(null);
  const engineRef = useRef(null);

  const audioRef = useRef(value._audio || {});
  const notesRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  const [showKeySigModal, setShowKeySigModal] = useState(false);
  const [keySigError, setKeySigError] = useState("");

  const [showTimeSigModal, setShowTimeSigModal] = useState(false);
  const [timeSigError, setTimeSigError] = useState("");

  const audioValue = audioRef.current;

  const initialParts = getDraftInitialTimeParts(value, config);

  const selectedTimeTop = initialParts.top;
  const selectedTimeBottom = initialParts.bottom;

  const selectedTimeSig = getDisplayedTimeSigFromValue(value);

  const [draftTimeSig, setDraftTimeSig] = useState({
    top: selectedTimeTop || "",
    bottom: selectedTimeBottom || ""
  });

  const [draftKeySigAccidentals, setDraftKeySigAccidentals] = useState(
    value.keySignatureAccidentals || {}
  );

  const selectedKeySignatureAccidentals =
    value.keySignatureAccidentals ??
    config.keySignatureAccidentals ??
    {};

  /* ─────────────────────────────────────────────
     ENGINE RELOAD
  ───────────────────────────────────────────── */

  function reloadEngine(nextValue = value) {
    if (!engineRef.current) return;

    const defaultTimeSig = getDefaultTimeSig(config);
    const displayedTimeSig = getDisplayedTimeSigFromValue(nextValue);

    const shouldHideTimeSig =
      config.allowStudentKeyTimeSignatureInput &&
      !displayedTimeSig;

    const { key, mode: keyMode } =
      getKeyAndModeFromValue(nextValue, config);

    engineRef.current.load({
      ...config,

      // Internal timing remains config/default-driven.
      tsig: defaultTimeSig,
      timeSig: defaultTimeSig,

      // Visual-only student-entered time signature.
      displayedTimeSig,

      hideTimeSignature: shouldHideTimeSig,

      key,
      mode: keyMode,

      keySignatureAccidentals:
        nextValue.keySignatureAccidentals ??
        config.keySignatureAccidentals ??
        {},

      melody: notesRef.current || nextValue.notes || config.melody
    });

    if (config.lockRhythm) {
      engineRef.current.hideDurButton();
    }
  }

  function saveTimeSignature() {
    if (mode !== "edit") return false;

    const top = String(draftTimeSig.top || "").replace(/[^0-9]/g, "");
    const bottom = String(draftTimeSig.bottom || "").replace(/[^0-9]/g, "");

    if (!isValidTimeSig(top, bottom)) {
      setTimeSigError("Invalid time signature");
      return false;
    }

    const tsig = `${top}/${bottom}`;

    const nextValue = {
      ...value,
      timeSigTop: top,
      timeSigBottom: bottom,
      tsig,
      timeSig: tsig,
      timeSignature: {
        top,
        bottom
      },
      _audio: audioRef.current
    };

    setTimeSigError("");
    onChange(nextValue);
    reloadEngine(nextValue);

    return true;
  }

  function saveKeySignature(nextAccidentals) {
    if (mode !== "edit") return false;

    const validation =
      getValidKeySignatureTypeAndCount(nextAccidentals);

    if (!validation.valid) {
      setKeySigError(validation.reason);
      return false;
    }

    const key = getMajorKeyFromSignature(
      validation.type,
      validation.count
    );

    const nextValue = {
      ...value,
      keySignatureAccidentals: nextAccidentals,
      keySignature: {
        type: validation.type,
        count: validation.count
      },
      key,
      mode: "major",
      _audio: audioRef.current
    };

    setKeySigError("");
    onChange(nextValue);
    reloadEngine(nextValue);

    return true;
  }

  /* ─────────────────────────────────────────────
     INIT ENGINE
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!containerRef.current) return;

    const defaultTimeSig = getDefaultTimeSig(config);

    engineRef.current = new melodyInput(
      "melody-editor" + config.number,
      containerRef.current,
      {
        measures: config.measures,

        // Internal timing remains config/default-driven.
        tsig: defaultTimeSig,
        timeSig: defaultTimeSig,

        // Visual-only student-entered time signature.
        displayedTimeSig: selectedTimeSig,

        hideTimeSignature:
          config.allowStudentKeyTimeSignatureInput &&
          !selectedTimeSig,

        clef: config.clef,
        lockPitch: config.lockPitch,
        lockRhythm: config.lockRhythm,
        fixedPitch: config.fixedPitch,
        keyText: config.keyText,
        key: value.key || config.key || "C",
        mode: value.mode || config.mode || "major",
        keySignatureAccidentals: selectedKeySignatureAccidentals,
        disabledMeasures: config.disabledMeasures || []
      },
      {
        setNotes: notes => {
          if (mode !== "edit") return;

          const frozenNotes = structuredClone(notes);
          notesRef.current = frozenNotes;

          // The notation engine is created once, so this callback otherwise
          // closes over the value from that first render. Spreading that stale
          // object can erase a key/time signature the student saved before
          // continuing to enter notes. Always merge into the latest answer.
          onChange({
            ...latestValueRef.current,
            notes: frozenNotes,
            _audio: audioRef.current
          });
        }
      }
    );

    return () => {
      engineRef.current = null;
      hasLoadedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─────────────────────────────────────────────
     LOAD ONCE
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!engineRef.current) return;
    if (hasLoadedRef.current) return;

    if (value?.notes) {
      notesRef.current = value.notes;
    }

    reloadEngine(value);

    hasLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, value?.notes]);

  /* ─────────────────────────────────────────────
     REVIEW MODE
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!engineRef.current) return;

    if (mode === "review") {
      engineRef.current.disabled = true;
    }
  }, [mode]);

  /* ─────────────────────────────────────────────
     AUDIO
  ───────────────────────────────────────────── */

  function setAudioState(audioState) {
    if (mode !== "edit") return;

    audioRef.current = audioState;

    onChange({
      ...value,
      _audio: audioState
    });
  }

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
      horizontalScrollRef={containerRef}
    >

      {showKeySigModal && (
        <>
          <div
            onClick={() => setShowKeySigModal(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 999
            }}
          />

          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              inset: "50px 0",
              background: "white",
              padding: 16,
              borderRadius: 8,
              maxWidth: 520,
              margin: "0 auto",
              maxHeight: "calc(100vh - 100px)",
              overflowY: "auto",
              overflowX: "hidden",
              zIndex: 1000,
              boxShadow: "0 12px 30px rgba(0,0,0,0.25)"
            }}
          >
            <KeySignatureInputWidget
              config={{
                clef: config.clef || "treble",
                slots: 1,
                prompt: config.keySignaturePromptName
                  ? `Draw ${config.keySignaturePromptName}`
                  : "Draw the key signature.",
                number: `${config.number}-KS`,
                points: config.keySignaturePoints ?? 1,
                allowLedgerLines: false
              }}
              value={{ accidentals: draftKeySigAccidentals }}
              onChange={(v) => {
                setDraftKeySigAccidentals(v.accidentals || {});
                setKeySigError("");
              }}
              mode="edit"
            />

            {keySigError && (
              <div
                style={{
                  marginTop: 8,
                  color: "#c62828",
                  fontWeight: 600
                }}
              >
                {keySigError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 12
              }}
            >
              <button
                type="button"
                onClick={() => setShowKeySigModal(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  const saved = saveKeySignature(
                    draftKeySigAccidentals
                  );

                  if (saved) {
                    setShowKeySigModal(false);
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}

      {showTimeSigModal && (
        <>
          <div
            onClick={() => setShowTimeSigModal(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 999
            }}
          />

          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              inset: "50px 0",
              background: "white",
              padding: 16,
              borderRadius: 8,
              maxWidth: 420,
              margin: "0 auto",
              maxHeight: "calc(100vh - 100px)",
              overflowY: "auto",
              overflowX: "hidden",
              zIndex: 1000,
              boxShadow: "0 12px 30px rgba(0,0,0,0.25)"
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              Draw Time Signature
            </h3>

            <div
              style={{
                display: "flex",
                gap: 16,
                alignItems: "center"
              }}
            >
              <div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  Top number
                </div>

                <input
                  value={draftTimeSig.top}
                  autoFocus
                  disabled={mode !== "edit"}
                  onChange={(e) => {
                    setDraftTimeSig(prev => ({
                      ...prev,
                      top: e.target.value.replace(/[^0-9]/g, "")
                    }));
                    setTimeSigError("");
                  }}
                  style={{
                    width: 70,
                    textAlign: "center",
                    fontSize: 24
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  Bottom number
                </div>

                <input
                  value={draftTimeSig.bottom}
                  disabled={mode !== "edit"}
                  onChange={(e) => {
                    setDraftTimeSig(prev => ({
                      ...prev,
                      bottom: e.target.value.replace(/[^0-9]/g, "")
                    }));
                    setTimeSigError("");
                  }}
                  style={{
                    width: 70,
                    textAlign: "center",
                    fontSize: 24
                  }}
                />
              </div>

              <div
                style={{
                  minWidth: 100,
                  minHeight: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  background: "#fafafa"
                }}
              >
                <TimeSigPreview
                  top={draftTimeSig.top}
                  bottom={draftTimeSig.bottom}
                />
              </div>
            </div>

            {timeSigError && (
              <div
                style={{
                  marginTop: 8,
                  color: "#c62828",
                  fontWeight: 600
                }}
              >
                {timeSigError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 16
              }}
            >
              <button
                type="button"
                onClick={() => setShowTimeSigModal(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  const saved = saveTimeSignature();

                  if (saved) {
                    setShowTimeSigModal(false);
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}

      {config.audio && (
        <div style={{ marginBottom: 12 }}>
          <AudioPlayButton
            src={config.audio}
            value={audioValue}
            onChange={setAudioState}
            mode={mode}
            maxListens={config.maxListens ?? 1}
          />
        </div>
      )}

      {config.image && (
        <div>
          <img width={1000} src={config.image} />
        </div>
      )}

      {config.allowStudentKeyTimeSignatureInput && (
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            marginBottom: 0,
            marginTop: 20,
            flexWrap: "wrap"
          }}
        >
          <button
            type="button"
            disabled={mode !== "edit"}
            onClick={() => {
              setDraftKeySigAccidentals(
                value.keySignatureAccidentals || {}
              );
              setKeySigError("");
              setShowKeySigModal(true);
            }}
          >
            Draw Key Signature
          </button>

          <button
            type="button"
            disabled={mode !== "edit"}
            onClick={() => {
              const parts = getDraftInitialTimeParts(value, config);

              setDraftTimeSig({
                top: parts.top || "",
                bottom: parts.bottom || ""
              });

              setTimeSigError("");
              setShowTimeSigModal(true);
            }}
          >
            Draw Time Signature
          </button>

          {/* {selectedTimeSig && (
            <span style={{ fontSize: 14 }}>
              Time Signature: {selectedTimeSig}
            </span>
          )} */}
        </div>
      )}

      <div
        ref={containerRef}
        id={"melody-editor" + config.number}
        style={{
          padding: 10,
          borderRadius: 6,
          maxWidth: 1100,
          overflow: "auto"
        }}
      />
    </QuestionLayout>
  );
}